import { useMemo, useCallback, useDeferredValue, useEffect, useRef, useState } from 'react';

import { Box, Paper, Skeleton, Stack } from '@mui/material';
import type { GridFilterModel, GridRowParams, GridSortModel } from '@mui/x-data-grid-pro';
import ExcelJS from 'exceljs';
import { MLDataGrid, DataGridErrorState, DataGridFilters, KPICards } from 'ml-react-component-library';
import type { AdvancedDateRangeValue, AdvancedFieldValue } from 'ml-react-component-library';
import { useNavigate, useSearchParams } from 'react-router';

import type {
  EngagementGridRow,
  IWaiverFilters,
  LetterType,
  RelatedClientGridRow,
  WaiverGridRow,
} from '@/apps/engagement-letter-database/data/types';
import { useFilterState } from '@/apps/engagement-letter-database/hooks/useFilterState';
import { engagementLetterDatabaseRoutes } from '@/apps/engagement-letter-database/routes';
import {
  useEngagementRecordsValue,
  usePagination,
  useWaiverRecordsValue,
} from '@/apps/engagement-letter-database/store';

import { createColumns, createRelatedClientColumns, createWaiverColumns } from '../../constants/index';

import CustomToolbar from './CustomToolbar';
import {
  ENGAGEMENT_ADVANCED_SECTIONS,
  ENGAGEMENT_FILTER_CONFIG,
  ENGAGEMENT_QUICK_FILTERS,
  RELATED_CLIENT_ADVANCED_SECTIONS,
  RELATED_CLIENT_FILTER_CONFIG,
  RELATED_CLIENT_QUICK_FILTERS,
  WAIVER_ADVANCED_SECTIONS,
  WAIVER_FILTER_CONFIG,
  WAIVER_QUICK_FILTERS,
} from './Filters/constants';
import { applyGridFilterModel } from './Filters/helpers/applyGridFilterModel';
import { matchesWaiverFilters } from './Filters/helpers/matchesWaiverFilters';
import { sortRows } from './Filters/helpers/sortRows';
import buildKpiCards from './KPICards';

const SKELETON_ROWS = 4;

const DEFAULT_ENGAGEMENT_SORT: GridSortModel = [{ field: 'clientName', sort: 'asc' }];
const DEFAULT_RELATED_CLIENT_SORT: GridSortModel = [{ field: 'relatedClientName', sort: 'asc' }];
const DEFAULT_WAIVER_SORT: GridSortModel = [{ field: 'clientName', sort: 'asc' }];
const RELATED_CLIENT_FILTER_PARAM_KEYS = [
  'relatedClientSearch',
  'relatedClientQuickFilter',
  'relatedClientLogicOperator',
  'relatedClientName',
  'relatedClientNumber',
  'relatedMemberClient',
  'relatedClientGridFilters',
] as const;

const parseRelatedClientGridFilterModel = (value: string | null): GridFilterModel => {
  if (!value) return { items: [] };
  try {
    const parsed = JSON.parse(value) as GridFilterModel;
    return Array.isArray(parsed.items) ? parsed : { items: [] };
  } catch {
    return { items: [] };
  }
};

const countGridFilters = (model: GridFilterModel) =>
  model.items.filter((item) => {
    if (!item.field) return false;
    if (Array.isArray(item.value)) return item.value.length > 0;
    return item.value != null && item.value !== '';
  }).length;

const DashboardDataGrid = ({ letterType }: { letterType: LetterType }) => {
  const { state, actions } = useFilterState();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const relatedClientParamsRef = useRef(searchParams);
  useEffect(() => {
    relatedClientParamsRef.current = searchParams;
  }, [searchParams]);
  const {
    engagementRecords,
    relatedClients,
    relatedClientTotalCount,
    isLoading,
    error: engagementError,
    refetch: refetchEngagement,
  } = useEngagementRecordsValue();
  const { page, pageSize, setPage, setPageSize } = usePagination();

  const [engagementSortModel, setEngagementSortModel] = useState<GridSortModel>(DEFAULT_ENGAGEMENT_SORT);
  const [engagementFilterModel, setEngagementFilterModel] = useState<GridFilterModel>({ items: [] });
  const [relatedClientSortModel, setRelatedClientSortModel] = useState<GridSortModel>(DEFAULT_RELATED_CLIENT_SORT);
  const [waiverSortModel, setWaiverSortModel] = useState<GridSortModel>(DEFAULT_WAIVER_SORT);
  const [waiverFilterModel, setWaiverFilterModel] = useState<GridFilterModel>({ items: [] });
  const relatedClientQuickFilterParam = searchParams.get('relatedClientQuickFilter');
  const relatedClientQuickFilter =
    relatedClientQuickFilterParam === 'multipleClients' || relatedClientQuickFilterParam === 'singleClient'
      ? relatedClientQuickFilterParam
      : null;
  const relatedClientSearchText = searchParams.get('relatedClientSearch') || '';
  const relatedClientLogicOperator = searchParams.get('relatedClientLogicOperator') === 'OR' ? 'OR' : 'AND';
  const relatedClientAdvancedValues = {
    relatedClientName: searchParams.get('relatedClientName') || '',
    relatedClientNumber: searchParams.get('relatedClientNumber') || '',
    memberClient: searchParams.get('relatedMemberClient') || '',
  };
  const relatedClientFilterModel = parseRelatedClientGridFilterModel(searchParams.get('relatedClientGridFilters'));

  const updateRelatedClientFilterParams = (update: (params: URLSearchParams) => void) => {
    // Clear all also emits a grid-filter change before the router re-renders.
    const next = new URLSearchParams(relatedClientParamsRef.current);
    update(next);
    relatedClientParamsRef.current = next;
    setSearchParams(next, { replace: true });
  };

  const setRelatedClientFilterParam = (key: string, value: string | null) => {
    updateRelatedClientFilterParams((params) => {
      if (value) params.set(key, value);
      else params.delete(key);
    });
  };

  // Deferred so the (potentially ~140k-row) synchronous filter recompute never
  // blocks the filter panel input, without adding a fixed artificial delay.
  const deferredEngagementFilterModel = useDeferredValue(engagementFilterModel);
  const deferredRelatedClientFilterModel = useDeferredValue(relatedClientFilterModel);
  const deferredWaiverFilterModel = useDeferredValue(waiverFilterModel);

  const searchText = searchParams.get('q') || '';
  const clientNumber = searchParams.get('clientNumber') || '';
  const clientName = searchParams.get('clientName') || '';
  const adverseClientName = searchParams.get('adverseClient') || '';
  const matterNumber = searchParams.get('matterNumber') || '';
  const adverseAttorney = searchParams.get('adverseAttorney') || '';
  const multipleWaiversFilter = searchParams.get('multipleWaiversFilter') || 'all';
  const executedFilter = state.executedFilter;
  const quickDatePreset = state.quickDatePreset;
  const logicOperator = searchParams.get('logicOperator') || 'AND';

  const activeWaiverQuickFilters = useMemo(() => {
    if (multipleWaiversFilter === 'yes') return ['multipleWaivers'];
    if (multipleWaiversFilter === 'no') return ['singleWaiver'];
    return [];
  }, [multipleWaiversFilter]);

  const activeEngagementQuickFilters = useMemo(() => {
    const keys: string[] = [];
    if (executedFilter === 'yes') keys.push('executed');
    else if (executedFilter === 'no') keys.push('notExecuted');
    if (quickDatePreset === 'open') keys.push('open');
    else if (quickDatePreset === 'closed') keys.push('closed');
    return keys;
  }, [executedFilter, quickDatePreset]);

  const activeEngagementAdvancedFilters = useMemo(
    () =>
      [
        state.clientName,
        state.clientNumber,
        state.openDateFrom || state.openDateTo,
        state.closedDateFrom || state.closedDateTo,
      ].filter(Boolean).length,
    [
      state.clientName,
      state.clientNumber,
      state.openDateFrom,
      state.openDateTo,
      state.closedDateFrom,
      state.closedDateTo,
    ],
  );

  const activeWaiverAdvancedFilters = useMemo(
    () =>
      [state.clientName, state.clientNumber, state.matterNumber, state.adverseClient, state.adverseAttorney].filter(
        Boolean,
      ).length,
    [state.clientName, state.clientNumber, state.matterNumber, state.adverseClient, state.adverseAttorney],
  );

  const activeSearchFilters = searchText ? 1 : 0;
  const activeEngagementFilterCount =
    activeSearchFilters +
    activeEngagementQuickFilters.length +
    activeEngagementAdvancedFilters +
    countGridFilters(engagementFilterModel);
  const activeWaiverFilterCount =
    activeSearchFilters +
    activeWaiverQuickFilters.length +
    activeWaiverAdvancedFilters +
    countGridFilters(waiverFilterModel);
  const activeRelatedClientQuickFilters = relatedClientQuickFilter ? [relatedClientQuickFilter] : [];
  const activeRelatedClientAdvancedFilters = Object.values(relatedClientAdvancedValues).filter(Boolean).length;
  const activeRelatedClientFilterCount =
    Number(Boolean(relatedClientSearchText)) +
    activeRelatedClientQuickFilters.length +
    activeRelatedClientAdvancedFilters +
    countGridFilters(relatedClientFilterModel);

  const handleWaiverQuickFilterToggle = (key: string) => {
    if (key === 'multipleWaivers') actions.setMultipleWaiversFilter(multipleWaiversFilter === 'yes' ? 'all' : 'yes');
    else if (key === 'singleWaiver') actions.setMultipleWaiversFilter(multipleWaiversFilter === 'no' ? 'all' : 'no');
    setPage(0);
  };

  const handleClearAll = useCallback(() => {
    actions.clearAll();
    setEngagementFilterModel({ items: [] });
    setWaiverFilterModel({ items: [] });
    setPage(0);
  }, [actions, setPage]);

  const handleRelatedClientClearAll = () => {
    updateRelatedClientFilterParams((params) => {
      RELATED_CLIENT_FILTER_PARAM_KEYS.forEach((key) => params.delete(key));
    });
    setPage(0);
  };

  const handleEngagementQuickFilterToggle = (key: string) => {
    switch (key) {
      case 'executed':
        actions.setExecutedFilter(executedFilter === 'yes' ? 'all' : 'yes');
        break;
      case 'notExecuted':
        actions.setExecutedFilter(executedFilter === 'no' ? 'all' : 'no');
        break;
      case 'open':
        actions.setQuickDatePreset(quickDatePreset === 'open' ? 'none' : 'open');
        break;
      case 'closed':
        actions.setQuickDatePreset(quickDatePreset === 'closed' ? 'none' : 'closed');
        break;
    }
    setPage(0);
  };

  const isWaiver = letterType === 'waiver';
  const advancedSections = isWaiver ? WAIVER_ADVANCED_SECTIONS : ENGAGEMENT_ADVANCED_SECTIONS;

  const openDateValue: AdvancedDateRangeValue = { from: state.openDateFrom, to: state.openDateTo };
  const closedDateValue: AdvancedDateRangeValue = { from: state.closedDateFrom, to: state.closedDateTo };

  const advancedValues: Record<string, AdvancedFieldValue> = isWaiver
    ? {
        clientName: state.clientName,
        clientNumber: state.clientNumber,
        matterNumber: state.matterNumber,
        adverseClient: state.adverseClient,
        adverseAttorney: state.adverseAttorney,
      }
    : {
        clientName: state.clientName,
        clientNumber: state.clientNumber,
        openDate: openDateValue,
        closedDate: closedDateValue,
      };

  const handleAdvancedChange = (fieldId: string, value: AdvancedFieldValue) => {
    const text = typeof value === 'string' ? value : '';
    switch (fieldId) {
      case 'clientName':
        actions.setClientName(text);
        break;
      case 'clientNumber':
        actions.setClientNumber(text);
        break;
      case 'matterNumber':
        actions.setMatterNumber(text);
        break;
      case 'adverseClient':
        actions.setAdverseClient(text);
        break;
      case 'adverseAttorney':
        actions.setAdverseAttorney(text);
        break;
      case 'openDate': {
        const range = (value ?? {}) as AdvancedDateRangeValue;
        actions.setOpenDateRange(range.from ?? '', range.to ?? '');
        break;
      }
      case 'closedDate': {
        const range = (value ?? {}) as AdvancedDateRangeValue;
        actions.setClosedDateRange(range.from ?? '', range.to ?? '');
        break;
      }
    }
    setPage(0);
  };

  const { waiverRows, isLoading: waiverLoading, error: waiverError, refetch: refetchWaivers } = useWaiverRecordsValue();

  // Helper function to extract attorney name
  const getAttorneyName = useCallback((attorney: (typeof engagementRecords)[number]['responsibleAttorney']): string => {
    if (!attorney) return 'N/A';
    if (typeof attorney === 'string') return attorney;
    return attorney.displayName || 'N/A';
  }, []);

  const engagementRows: EngagementGridRow[] = useMemo(
    () =>
      engagementRecords.map((record, index) => ({
        id: index + 1,
        clientName: record.clientName || 'Unknown',
        clientNumber: record.clientNumber,
        responsibleAttorney: getAttorneyName(record.responsibleAttorney),
        practiceGroup: record.practiceGroup || 'N/A',
        openDate: record.clientOpenDate ? record.clientOpenDate.toDate() : null,
        closedDate: record.clientClosedDate ? record.clientClosedDate.toDate() : null,
        isExecuted: record.isExecuted,
      })),
    [engagementRecords, getAttorneyName],
  );

  const relatedClientRows: RelatedClientGridRow[] = useMemo(
    () =>
      relatedClients.map((record, index) => ({
        id: record.RelatedClientNumber || `related-client-${index}`,
        relatedClientName: record.RelatedClientName || 'Unknown',
        relatedClientNumber: record.RelatedClientNumber || 'N/A',
        clients: record.Clients.map((client) => client.ClientName1 || client.ClientNumber).join(', '),
        clientCount: record.Clients.length,
      })),
    [relatedClients],
  );

  const handleRelatedClientQuickFilterToggle = (key: string) => {
    if (key === 'multipleClients' || key === 'singleClient') {
      setRelatedClientFilterParam('relatedClientQuickFilter', relatedClientQuickFilter === key ? null : key);
      setPage(0);
    }
  };

  const handleRelatedClientAdvancedChange = (fieldId: string, value: AdvancedFieldValue) => {
    const text = typeof value === 'string' ? value : '';
    if (fieldId in relatedClientAdvancedValues) {
      const params: Record<string, string> = {
        relatedClientName: 'relatedClientName',
        relatedClientNumber: 'relatedClientNumber',
        memberClient: 'relatedMemberClient',
      };
      setRelatedClientFilterParam(params[fieldId], text || null);
      setPage(0);
    }
  };

  const handleRelatedClientGridFilterModelChange = (model: GridFilterModel) => {
    const hasFilters = countGridFilters(model) > 0 || (model.quickFilterValues ?? []).some(Boolean);
    setRelatedClientFilterParam('relatedClientGridFilters', hasFilters ? JSON.stringify(model) : null);
    setPage(0);
  };

  const paneFilteredRelatedClientRows = useMemo(() => {
    const searchTerm = relatedClientSearchText.trim().toLowerCase();
    const relatedClientName = relatedClientAdvancedValues.relatedClientName.trim().toLowerCase();
    const relatedClientNumber = relatedClientAdvancedValues.relatedClientNumber.trim().toLowerCase();
    const memberClient = relatedClientAdvancedValues.memberClient.trim().toLowerCase();

    return relatedClientRows.filter((row) => {
      const checks: boolean[] = [];
      if (searchTerm) {
        checks.push(
          `${row.relatedClientName} ${row.relatedClientNumber} ${row.clients}`.toLowerCase().includes(searchTerm),
        );
      }
      if (relatedClientName) checks.push(row.relatedClientName.toLowerCase().includes(relatedClientName));
      if (relatedClientNumber) checks.push(row.relatedClientNumber.toLowerCase().includes(relatedClientNumber));
      if (memberClient) checks.push(row.clients.toLowerCase().includes(memberClient));

      const matchesFilters =
        checks.length === 0 || (relatedClientLogicOperator === 'OR' ? checks.some(Boolean) : checks.every(Boolean));
      if (!matchesFilters) return false;
      if (relatedClientQuickFilter === 'multipleClients' && row.clientCount <= 1) return false;
      if (relatedClientQuickFilter === 'singleClient' && row.clientCount !== 1) return false;
      return true;
    });
  }, [
    relatedClientRows,
    relatedClientSearchText,
    relatedClientLogicOperator,
    relatedClientAdvancedValues,
    relatedClientQuickFilter,
  ]);

  const filteredRelatedClientRows = useMemo(
    () =>
      applyGridFilterModel(
        paneFilteredRelatedClientRows,
        deferredRelatedClientFilterModel,
        RELATED_CLIENT_FILTER_CONFIG,
      ),
    [paneFilteredRelatedClientRows, deferredRelatedClientFilterModel],
  );

  const sortedRelatedClientRows = useMemo(
    () => sortRows(filteredRelatedClientRows, relatedClientSortModel),
    [filteredRelatedClientRows, relatedClientSortModel],
  );

  const pagedRelatedClientRows = useMemo(
    () => sortedRelatedClientRows.slice(page * pageSize, page * pageSize + pageSize),
    [sortedRelatedClientRows, page, pageSize],
  );

  const relatedClientPaneKey = `${relatedClientSearchText}|${relatedClientLogicOperator}|${relatedClientQuickFilter}|${JSON.stringify(relatedClientAdvancedValues)}`;
  const prevRelatedClientPaneKey = useRef(relatedClientPaneKey);
  useEffect(() => {
    if (prevRelatedClientPaneKey.current !== relatedClientPaneKey) {
      prevRelatedClientPaneKey.current = relatedClientPaneKey;
      setPage(0);
    }
  }, [relatedClientPaneKey, setPage]);

  const handleFilterModelChange = (model: GridFilterModel) => {
    setEngagementFilterModel(model);
    setPage(0);
  };

  // Quick-filter (executed / open-closed) predicates applied client-side.
  const paneFilteredEngagementRows = useMemo(
    () =>
      engagementRows.filter((row) => {
        if (executedFilter === 'yes' && row.isExecuted !== true) return false;
        if (executedFilter === 'no' && row.isExecuted === true) return false;
        if (quickDatePreset === 'open' && row.closedDate != null) return false;
        if (quickDatePreset === 'closed' && row.closedDate == null) return false;
        return true;
      }),
    [engagementRows, executedFilter, quickDatePreset],
  );

  // Apply the grid's column filters over the full (FilterPane-filtered) dataset.
  const filteredEngagementRows = useMemo(
    () => applyGridFilterModel(paneFilteredEngagementRows, deferredEngagementFilterModel, ENGAGEMENT_FILTER_CONFIG),
    [paneFilteredEngagementRows, deferredEngagementFilterModel],
  );

  const sortedEngagementRows = useMemo(
    () => sortRows(filteredEngagementRows, engagementSortModel),
    [filteredEngagementRows, engagementSortModel],
  );

  // Hand MUI only the current page slice.
  const pagedEngagementRows = useMemo(
    () => sortedEngagementRows.slice(page * pageSize, page * pageSize + pageSize),
    [sortedEngagementRows, page, pageSize],
  );

  // Full waiver dataset comes from the shared store (loaded once at the
  // Dashboard and persisted across navigation). All filtering, sorting and
  // pagination happen client-side (mirrors the engagement grid).
  const allWaiverRows: WaiverGridRow[] = waiverRows;

  // FilterPane (search bar / advanced) predicates applied client-side.
  const paneFilteredWaiverRows = useMemo(() => {
    const filters: IWaiverFilters = {
      searchText,
      clientNumber,
      clientName,
      adverseClientName,
      matterNumber,
      adverseAttorney,
      multipleWaivers: multipleWaiversFilter ?? 'all',
      logicOperator,
    };
    return allWaiverRows.filter((row) => matchesWaiverFilters(row, filters));
  }, [
    allWaiverRows,
    searchText,
    clientNumber,
    clientName,
    adverseClientName,
    matterNumber,
    adverseAttorney,
    multipleWaiversFilter,
    logicOperator,
  ]);

  const filteredWaiverRows = useMemo(
    () => applyGridFilterModel(paneFilteredWaiverRows, deferredWaiverFilterModel, WAIVER_FILTER_CONFIG),
    [paneFilteredWaiverRows, deferredWaiverFilterModel],
  );

  const sortedWaiverRows = useMemo(
    () => sortRows(filteredWaiverRows, waiverSortModel),
    [filteredWaiverRows, waiverSortModel],
  );

  const pagedWaiverRows = useMemo(
    () => sortedWaiverRows.slice(page * pageSize, page * pageSize + pageSize),
    [sortedWaiverRows, page, pageSize],
  );

  const handleWaiverFilterModelChange = (model: GridFilterModel) => {
    setWaiverFilterModel(model);
    setPage(0);
  };

  // Reset to the first page whenever the active FilterPane waiver filters change so
  // the client-side paginated grid never lands on an out-of-range page.
  const waiverPaneKey = `${searchText}|${clientNumber}|${clientName}|${adverseClientName}|${matterNumber}|${adverseAttorney}|${multipleWaiversFilter}|${logicOperator}`;
  const prevWaiverPaneKey = useRef(waiverPaneKey);
  useEffect(() => {
    if (letterType === 'waiver' && prevWaiverPaneKey.current !== waiverPaneKey) {
      prevWaiverPaneKey.current = waiverPaneKey;
      setPage(0);
    }
  }, [letterType, waiverPaneKey, setPage]);

  const engagementColumns = useMemo(() => createColumns(), []);
  const relatedClientColumns = useMemo(() => createRelatedClientColumns(), []);
  const waiverColumns = useMemo(() => createWaiverColumns(), []);
  const isRelatedClientView = searchParams.get('engagementLetterView') !== 'clients';

  const handleRelatedClientFilterModelChange = (model: GridFilterModel) => {
    handleRelatedClientGridFilterModelChange(model);
  };

  const handlePaginationChange = (model: { page: number; pageSize: number }) => {
    setPage(model.page);
    setPageSize(model.pageSize);
  };

  const handleRowClick = (params: GridRowParams<EngagementGridRow>) => {
    const clientPath = engagementLetterDatabaseRoutes.client.path.replace(':clientId', params.row.clientNumber);
    void navigate(clientPath);
  };
  const handleRelatedClientRowClick = (params: GridRowParams<RelatedClientGridRow>) => {
    if (params.row.relatedClientNumber === 'N/A') return;
    const relatedClientPath = engagementLetterDatabaseRoutes.relatedClient.path.replace(
      ':relatedClientId',
      params.row.relatedClientNumber,
    );
    void navigate(relatedClientPath);
  };
  const handleWaiverRowClick = (params: GridRowParams<WaiverGridRow>) => {
    const waiverPath = engagementLetterDatabaseRoutes.waiver.path.replace(':waiverId', String(params.row.id));
    void navigate(waiverPath);
  };

  // Exports the full filtered/sorted dataset for the active letter type (not just
  // the current page) to an .xlsx file.
  const handleExport = useCallback(async () => {
    const workbook = new ExcelJS.Workbook();
    const isWaiver = letterType === 'waiver';
    const worksheet = workbook.addWorksheet(
      isWaiver ? 'Waiver Letters' : isRelatedClientView ? 'Related Clients' : 'Engagement Letters',
    );

    if (isWaiver) {
      worksheet.columns = [
        { header: 'Client Name', key: 'clientName', width: 30 },
        { header: 'Client Number', key: 'clientNumber', width: 18 },
        { header: 'Matter Number', key: 'matterNumber', width: 18 },
        { header: 'Adverse Client', key: 'adverseClientName', width: 30 },
        { header: 'Adverse Contact Signer', key: 'adverseContactSigner', width: 28 },
        { header: 'Adverse Letter Date', key: 'adverseLetterDate', width: 18 },
        { header: 'Adverse MLB Attorney', key: 'adverseAttorney', width: 28 },
        { header: 'Client Contact', key: 'clientContact', width: 24 },
        { header: 'Client Letter Date', key: 'clientLetterDate', width: 18 },
        { header: 'Client MLB Attorney', key: 'clientAttorney', width: 28 },
        { header: 'Multiple Waivers', key: 'multipleWaivers', width: 16 },
      ];
      worksheet.getRow(1).font = { bold: true };

      sortedWaiverRows.forEach((row) => {
        worksheet.addRow({
          clientName: row.clientName,
          clientNumber: row.clientNumber,
          matterNumber: row.matterNumber,
          adverseClientName: row.adverseClientName,
          adverseContactSigner: row.adverseContactSigner,
          adverseLetterDate: row.adverseLetterDate ? row.adverseLetterDate.toLocaleDateString() : '',
          adverseAttorney: row.adverseAttorney,
          clientContact: row.clientContact,
          clientLetterDate: row.clientLetterDate ? row.clientLetterDate.toLocaleDateString() : '',
          clientAttorney: row.clientAttorney,
          multipleWaivers: row.multipleWaivers,
        });
      });
    } else if (isRelatedClientView) {
      worksheet.columns = [
        { header: 'Related Client Name', key: 'relatedClientName', width: 30 },
        { header: 'Related Client Number', key: 'relatedClientNumber', width: 22 },
        { header: 'Clients', key: 'clients', width: 50 },
        { header: 'Client Count', key: 'clientCount', width: 14 },
      ];
      worksheet.getRow(1).font = { bold: true };

      sortedRelatedClientRows.forEach((row) => {
        worksheet.addRow({
          relatedClientName: row.relatedClientName,
          relatedClientNumber: row.relatedClientNumber,
          clients: row.clients,
          clientCount: row.clientCount,
        });
      });
    } else {
      worksheet.columns = [
        { header: 'Client Name', key: 'clientName', width: 30 },
        { header: 'Client Number', key: 'clientNumber', width: 18 },
        { header: 'Responsible Attorney', key: 'responsibleAttorney', width: 28 },
        { header: 'Practice', key: 'practiceGroup', width: 20 },
        { header: 'Open Date', key: 'openDate', width: 15 },
        { header: 'Closed Date', key: 'closedDate', width: 15 },
        { header: 'Executed', key: 'isExecuted', width: 12 },
      ];
      worksheet.getRow(1).font = { bold: true };

      sortedEngagementRows.forEach((row) => {
        worksheet.addRow({
          clientName: row.clientName,
          clientNumber: row.clientNumber,
          responsibleAttorney: row.responsibleAttorney,
          practiceGroup: row.practiceGroup,
          openDate: row.openDate ? row.openDate.toLocaleDateString() : '',
          closedDate: row.closedDate ? row.closedDate.toLocaleDateString() : 'Open',
          isExecuted: row.isExecuted ? 'Yes' : 'No',
        });
      });
    }

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    const filePrefix = isWaiver ? 'conflict-waivers' : isRelatedClientView ? 'related-clients' : 'engagement-letters';
    link.download = `${filePrefix}-${new Date().toISOString().slice(0, 10)}.xlsx`;
    link.click();
    URL.revokeObjectURL(url);
  }, [isRelatedClientView, letterType, sortedEngagementRows, sortedRelatedClientRows, sortedWaiverRows]);

  const currentLoading = letterType === 'waiver' ? waiverLoading : isLoading;
  const currentError = letterType === 'waiver' ? waiverError : engagementError;

  const kpiCards = buildKpiCards({
    letterType,
    isRelatedClientView,
    relatedClientRows: sortedRelatedClientRows,
    relatedClientTotalCount,
    sortedWaiverRows,
    sortedEngagementRows,
  });

  if (currentError) {
    return (
      <Paper elevation={2}>
        <DataGridErrorState
          message={currentError.message || 'Failed to load records. Please try again.'}
          onRetry={() => {
            letterType === 'waiver' ? refetchWaivers() : refetchEngagement();
          }}
        />
      </Paper>
    );
  }

  if (currentLoading) {
    return (
      <Paper elevation={2} sx={{ p: 2 }}>
        <Stack spacing={1}>
          <Skeleton variant="rectangular" height={56} />
          {Array.from({ length: SKELETON_ROWS }, (_, i) => (
            <Skeleton key={i} variant="rectangular" height={52} />
          ))}
        </Stack>
      </Paper>
    );
  }

  if (letterType !== 'waiver' && isRelatedClientView) {
    return (
      <Box>
        <Box sx={{ mb: 2 }}>
          <KPICards cards={kpiCards} />
        </Box>
        <DataGridFilters
          toggleOpen
          targetFocus="search"
          searchValue={relatedClientSearchText}
          onSearchChange={(value) => {
            setRelatedClientFilterParam('relatedClientSearch', value || null);
            setPage(0);
          }}
          searchPlaceholder="Search related client or member client..."
          quickFilters={RELATED_CLIENT_QUICK_FILTERS}
          activeQuickFilters={activeRelatedClientQuickFilters}
          onQuickFilterToggle={handleRelatedClientQuickFilterToggle}
          onClearAll={handleRelatedClientClearAll}
          activeFilterCount={activeRelatedClientFilterCount}
          showLogicOperator
          logicOperator={relatedClientLogicOperator}
          onLogicOperatorChange={(value) => {
            setRelatedClientFilterParam('relatedClientLogicOperator', value === 'AND' ? null : value);
            setPage(0);
          }}
          advancedSections={RELATED_CLIENT_ADVANCED_SECTIONS}
          advancedValues={relatedClientAdvancedValues}
          onAdvancedChange={handleRelatedClientAdvancedChange}
          gridFilterModel={relatedClientFilterModel}
          onGridFilterModelChange={handleRelatedClientFilterModelChange}
        />
        <Paper elevation={2}>
          <MLDataGrid
            showToolbar
            slots={{ toolbar: CustomToolbar }}
            slotProps={{
              toolbar: { onExport: () => void handleExport() },
            }}
            rows={pagedRelatedClientRows}
            columns={relatedClientColumns}
            onRowClick={handleRelatedClientRowClick}
            pagination
            paginationMode="server"
            rowCount={sortedRelatedClientRows.length}
            paginationModel={{ page, pageSize }}
            onPaginationModelChange={handlePaginationChange}
            sortingMode="server"
            sortModel={relatedClientSortModel}
            onSortModelChange={(model) => {
              setRelatedClientSortModel(model);
              setPage(0);
            }}
            filterMode="server"
            filterModel={relatedClientFilterModel}
            onFilterModelChange={handleRelatedClientFilterModelChange}
            localeText={{
              paginationDisplayedRows: ({ from, to, count }) =>
                activeRelatedClientFilterCount
                  ? `${from}-${to} of ${count.toLocaleString()} loaded (${relatedClientTotalCount.toLocaleString()} total)`
                  : `${from}-${to} of ${relatedClientTotalCount.toLocaleString()} total (${count.toLocaleString()} loaded)`,
            }}
            pageSizeOptions={[50, 100, 250, 500]}
          />
        </Paper>
      </Box>
    );
  }

  return (
    <Box>
      {letterType === 'waiver' ? (
        <Box>
          <Box sx={{ mb: 2 }}>
            <KPICards cards={kpiCards} />
          </Box>
          <DataGridFilters
            toggleOpen
            targetFocus="search"
            searchValue={state.searchText}
            onSearchChange={actions.setSearchText}
            searchPlaceholder="Search matter name or matter number..."
            quickFilters={WAIVER_QUICK_FILTERS}
            activeQuickFilters={activeWaiverQuickFilters}
            onQuickFilterToggle={handleWaiverQuickFilterToggle}
            onClearAll={handleClearAll}
            activeFilterCount={activeWaiverFilterCount}
            showLogicOperator
            logicOperator={state.logicOperator}
            onLogicOperatorChange={actions.setLogicOperator}
            advancedSections={advancedSections}
            advancedValues={advancedValues}
            onAdvancedChange={handleAdvancedChange}
            gridFilterModel={waiverFilterModel}
            onGridFilterModelChange={handleWaiverFilterModelChange}
          />
          <Paper elevation={2}>
            <MLDataGrid
              showToolbar
              slots={{
                toolbar: CustomToolbar,
              }}
              slotProps={{
                toolbar: { onExport: () => void handleExport() },
              }}
              rows={pagedWaiverRows}
              columns={waiverColumns}
              pagination
              paginationMode="server"
              rowCount={sortedWaiverRows.length}
              paginationModel={{ page, pageSize }}
              onPaginationModelChange={handlePaginationChange}
              sortingMode="server"
              sortModel={waiverSortModel}
              onSortModelChange={(model) => {
                setWaiverSortModel(model);
                setPage(0);
              }}
              filterMode="server"
              filterModel={waiverFilterModel}
              onFilterModelChange={handleWaiverFilterModelChange}
              pageSizeOptions={[50, 100, 250, 500]}
              onRowClick={handleWaiverRowClick}
              loading={waiverLoading}
            />
          </Paper>
        </Box>
      ) : (
        <Box>
          <Box sx={{ mb: 2 }}>
            <KPICards cards={kpiCards} />
          </Box>
          <DataGridFilters
            toggleOpen
            targetFocus="search"
            searchValue={state.searchText}
            onSearchChange={actions.setSearchText}
            searchPlaceholder="Search client name or client number..."
            quickFilters={ENGAGEMENT_QUICK_FILTERS}
            activeQuickFilters={activeEngagementQuickFilters}
            onQuickFilterToggle={handleEngagementQuickFilterToggle}
            onClearAll={handleClearAll}
            activeFilterCount={activeEngagementFilterCount}
            showLogicOperator
            logicOperator={state.logicOperator}
            onLogicOperatorChange={actions.setLogicOperator}
            advancedSections={advancedSections}
            advancedValues={advancedValues}
            onAdvancedChange={handleAdvancedChange}
            gridFilterModel={engagementFilterModel}
            onGridFilterModelChange={handleFilterModelChange}
          />
          <Paper elevation={2}>
            <MLDataGrid
              showToolbar
              slots={{
                toolbar: CustomToolbar,
              }}
              slotProps={{
                toolbar: { onExport: () => void handleExport() },
              }}
              rows={pagedEngagementRows}
              columns={engagementColumns}
              pagination
              paginationMode="server"
              rowCount={sortedEngagementRows.length}
              paginationModel={{ page, pageSize }}
              onPaginationModelChange={handlePaginationChange}
              sortingMode="server"
              sortModel={engagementSortModel}
              onSortModelChange={(model) => {
                setEngagementSortModel(model);
                setPage(0);
              }}
              filterMode="server"
              filterModel={engagementFilterModel}
              onFilterModelChange={handleFilterModelChange}
              pageSizeOptions={[50, 100, 250, 500]}
              onRowClick={handleRowClick}
              loading={isLoading}
            />
          </Paper>
        </Box>
      )}
    </Box>
  );
};

export default DashboardDataGrid;
